import {
  Box,
  ButtonBase,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Button } from "components/button";
import { DownArrowIcon, LeftArrowIcon, RightArrowIcon } from "components/icons";
import { Popover } from "components/popover";
import { useGlobalStore } from "controller";
import type { Dayjs } from "dayjs";
import { useAtom, useSetAtom } from "jotai";
import { getFormattedDate } from "utilz/helpers";
import {
  calendarAnchorDateAtom,
  calendarSelectedDateAtom,
  DATE_KEY_FORMAT,
  mspDay,
  mspNow,
} from "../../store";

interface WeekStripProps {
  weekDays: Dayjs[];
  selectedDate: string;
}

/**
 * Month label + week paging on top, the seven days of the current week below.
 * Selecting a day scrolls its group into view in the event list; the month
 * label opens a full calendar for jumping to any other week.
 */
export const WeekStrip = ({ weekDays, selectedDate }: WeekStripProps) => {
  const theme = useTheme();
  const timezone = useGlobalStore((state) => state.timezone);
  const dateFormat = useGlobalStore((state) => state.dateFormat);
  const [anchorDate, setAnchorDate] = useAtom(calendarAnchorDateAtom);
  const setSelectedDate = useSetAtom(calendarSelectedDateAtom);

  const anchor = mspDay(anchorDate, timezone);
  const today = mspNow(timezone).format(DATE_KEY_FORMAT);

  const shiftWeek = (weeks: number) => {
    setAnchorDate(anchor.add(weeks, "week").format(DATE_KEY_FORMAT));
  };

  // Picking a day in the full calendar moves the strip to that day's week and
  // selects it, so the event list scrolls straight to it.
  const pickDate = (value: Dayjs | null, closePopover: () => void) => {
    if (!value?.isValid()) return;

    const date = value.format(DATE_KEY_FORMAT);
    setAnchorDate(date);
    setSelectedDate(date);
    closePopover();
  };

  return (
    <Stack gap="12px" sx={{ px: "8px" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Popover
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          paperSx={{ mt: "4px", borderRadius: "8px" }}
          popoverContent={(closePopover) => (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                value={anchor}
                timezone={timezone || undefined}
                onChange={(value) => pickDate(value, closePopover)}
                sx={{
                  "& .MuiPickersDay-root": {
                    ...theme.typography["body-small"],
                    borderRadius: "6px",
                    ":hover": {
                      backgroundColor: theme.palette.neutral["005"],
                    },
                    "&.Mui-selected": {
                      backgroundColor:
                        theme.palette.primitive.brand.purple[600],
                      color: theme.palette.neutral.main,
                      ":hover": {
                        backgroundColor: theme.palette.primary.hover,
                      },
                    },
                    "&.MuiPickersDay-today": {
                      border: `1px solid ${theme.palette.primary.main}`,
                    },
                  },
                }}
              />
            </LocalizationProvider>
          )}
        >
          <Button
            variant="ghost"
            aria-label="Select date"
            aria-haspopup="true"
            endIcon={
              <DownArrowIcon
                sx={{ fontSize: "16px", color: theme.palette.lightBg.medium }}
              />
            }
            sx={{
              minWidth: "0px",
              minHeight: "0px",
              color: theme.palette.lightBg.high,
            }}
          >
            {anchor.format("MMMM YYYY")}
          </Button>
        </Popover>

        <Stack direction="row" alignItems="center" gap="2px">
          <IconButton
            size="small"
            aria-label="Previous week"
            onClick={() => shiftWeek(-1)}
          >
            <LeftArrowIcon
              sx={{ fontSize: "18px", color: theme.palette.lightBg.medium }}
            />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Next week"
            onClick={() => shiftWeek(1)}
          >
            <RightArrowIcon
              sx={{ fontSize: "18px", color: theme.palette.lightBg.medium }}
            />
          </IconButton>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          alignItems: "start",
        }}
      >
        {weekDays.map((day) => {
          const date = day.format(DATE_KEY_FORMAT);
          const isSelected = date === selectedDate;
          const isToday = date === today;
          const isPast = date < today;

          return (
            <ButtonBase
              key={date}
              onClick={() => setSelectedDate(date)}
              aria-label={`${day.format("dddd")}, ${getFormattedDate({
                date: day.toISOString(),
                format: dateFormat,
              })}`}
              aria-current={isToday ? "date" : undefined}
              sx={{
                flexDirection: "column",
                gap: "4px",
                py: "4px",
                borderRadius: "6px",
                "&:hover .day-number": {
                  backgroundColor: isSelected
                    ? theme.palette.primary.hover
                    : theme.palette.neutral["005"],
                },
              }}
            >
              <Box
                className="day-number"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "26px",
                  height: "26px",
                  borderRadius: "100px",
                  backgroundColor: isSelected
                    ? theme.palette.primary.main
                    : "transparent",
                }}
              >
                <Typography
                  variant="caption-2"
                  sx={{
                    color: isSelected
                      ? theme.palette.neutral.main
                      : isPast
                        ? theme.palette.darkBg.low
                        : theme.palette.lightBg.high,
                  }}
                >
                  {day.format("DD")}
                </Typography>
              </Box>
              <Typography
                variant="body-xsmall"
                sx={{
                  color: isPast
                    ? theme.palette.darkBg.low
                    : theme.palette.lightBg.low,
                }}
              >
                {day.format("ddd")}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Stack>
  );
};
